import { INoDataResponse } from '../../../../core/interfaces/INoDataResponse';

/** CultureKit visit rule: show must start within this many minutes. */
export const CINEVILLE_MAX_CHECK_IN_MINUTES = 900;

const CINEVILLE_VALIDATION_ERROR_CODES = new Set([
  'PartnerCodeValidationFailed',
  'CinevilleVisitValidationFailed'
]);

const CINEVILLE_CAUSE_CODE_DEFAULT_MESSAGES: Record<string, string> = {
  'ServiceRule.CardExpired': 'Die Karte ist abgelaufen.',
  'ServiceRule.CardNotActive': 'Die Karte ist nicht aktiv.',
  'ServiceRule.SubscriptionNotActive': 'Das Abonnement ist nicht aktiv.',
  'ServiceRule.SubscriptionNotYetValid': 'Das Abonnement ist noch nicht gültig.',
  'ServiceRule.SubscriptionExpired': 'Das Abonnement ist abgelaufen.',
  'ServiceRule.LocationNotAllowed': 'Die Karte gilt nicht für diesen Standort.',
  'ServiceRule.FilmNotAllowed': 'Die Karte gilt nicht für diesen Film.',
  'ServiceRule.ShowNotAllowed': 'Die Karte gilt nicht für diese Vorstellung.',
  'ServiceRule.CheckInTooLate':
    'Der Check-in-Zeitraum für diese Vorstellung ist bereits abgelaufen.',
  'ServiceRule.CheckInTooEarly':
    'Der Check-in für diese Vorstellung ist noch nicht möglich.',
  'ServiceRule.ShowVisitLimitReached':
    'Die maximale Besucherzahl für diese Vorstellung ist erreicht.',
  'ServiceRule.ShowGroupVisitLimitReached':
    'Die maximale Besucherzahl für diese Vorstellungsgruppe ist erreicht.',
  'ServiceRule.InsufficientTimeBetweenVisits':
    'Der Mindestabstand zum letzten Besuch ist noch nicht erreicht.',
  'ServiceRule.ProductNotYetValid': 'Das Produkt ist noch nicht gültig.',
  'ServiceRule.ProductExpired': 'Das Produkt ist abgelaufen.',
  'Reservation.InsufficientTimeBetweenReservations':
    'Zwischen zwei Reservierungen muss mehr Zeit liegen.',
  'Reservation.CustomerReservationLimitReached':
    'Die maximale Anzahl aktiver Reservierungen für diese Person ist erreicht.',
  'Reservation.EventReservationLimitReached':
    'Die Reservierungskapazität für diese Vorstellung ist erschöpft.',
  'Reservation.ReservationsForEventNotSupported':
    'Für diese Vorstellung sind keine Reservierungen möglich.',
  'Reservation.InsufficientTimeBeforeEvent':
    'Die Vorstellung beginnt zu bald für eine Reservierung.',
  'Reservation.ReservationsDisabledForCustomer':
    'Reservierungen sind für diese Person deaktiviert.',
  'Reservation.EventAlreadyStarted': 'Die Vorstellung hat bereits begonnen.',
  'Reservation.CustomerHasReservationForSameEvent':
    'Für diese Person besteht bereits eine aktive Reservierung für diese Vorstellung.',
  'Reservation.CollectionDeadlineTooSoon':
    'Die Abholfrist liegt zu nah am aktuellen Zeitpunkt.',
  'Reservation.ReservationNotActive': 'Die Reservierung ist nicht aktiv.',
  'Reservation.CollectionDeadlineAnticipation':
    'Die neue Abholfrist darf nicht vor der bisherigen liegen.',
  'Reservation.InvalidCancellationReason': 'Der Stornierungsgrund ist ungültig.',
  'Reservation.CollectionDeadlineNotReached':
    'Die Abholfrist ist noch nicht abgelaufen.',
  'Reservation.CardNotFound': 'Die Karte wurde nicht gefunden.',
  'Reservation.EventNotFound':
    'Die Vorstellung wurde bei Cineville nicht gefunden.'
};

const CINEVILLE_UNKNOWN_CAUSE_HEADER =
  'Cineville hat den Partnercode abgelehnt.';

function joinCinevilleMessageParts(parts: string[]): string {
  return parts.filter(part => part.length > 0).join(' ');
}

export class CinevilleUtils {

  public static isCinevilleCheckInTooEarly(
    showBegin: string | Date | null | undefined
  ): boolean {
    if (!showBegin) {
      return false;
    }

    const beginMs = new Date(showBegin).getTime();
    if (Number.isNaN(beginMs)) {
      return false;
    }

    const minutesUntilShow = (beginMs - Date.now()) / 60_000;
    return minutesUntilShow > CINEVILLE_MAX_CHECK_IN_MINUTES;
  }

  public static getCinevilleCheckInTooEarlyMessage(): string {
    return 'Cineville tickets cannot be sold more than [[maxMinutes]] minutes before the show starts.'.trans(
      'cart/cineville/CheckInTooEarly.Message',
      [['maxMinutes', String(CINEVILLE_MAX_CHECK_IN_MINUTES)]]
    );
  }

  public static resolveCinevilleErrorMessage(
    error: INoDataResponse | undefined | null
  ): string | null {
    if (!error?.message || !CINEVILLE_VALIDATION_ERROR_CODES.has(error.message)) {
      return null;
    }

    const errors = error.errors;
    if (!errors?.length) {
      return null;
    }

    const code = errors[0] ?? '';
    const detail = errors[1] ?? '';

    if (!code && !detail) {
      return null;
    }

    if (code && CINEVILLE_CAUSE_CODE_DEFAULT_MESSAGES[code]) {
      const main = CINEVILLE_CAUSE_CODE_DEFAULT_MESSAGES[code].trans(
        `vouchers/external/cineville/notifications/${code}.Message`
      );

      return detail ? joinCinevilleMessageParts([main, detail]) : main;
    }

    const header = CINEVILLE_UNKNOWN_CAUSE_HEADER.trans(
      'vouchers/external/cineville/notifications/Unknown.Message'
    );

    if (code) {
      return joinCinevilleMessageParts([header, detail, `(${code})`]);
    }

    return joinCinevilleMessageParts([header, detail]);
  }
}
